import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, FormProvider } from '@tq/ui-components/dist/components/forms-engine';
import { DatePicker, Flex, FormControl, FormLabel, Grid, isArray } from '@tq/ui-components';

import { useIsMobile } from '@tq/ui-components/dist/hooks';
import { addDays } from 'date-fns';
import { useLocaleDatePickerFormat } from 'config/DateFormats/useDatePickerFormat';

type DateFilterProps = {
  groupTitle?: string;
  initialStartDate?: Date;
  initialEndDate?: Date;
  onSetStartDate: (date: Date) => void;
  onSetEndDate: (date: Date) => void;
  onSubmit?: () => void;
  fromDateLabel?: string;
  toDateLabel?: string;
};

export const DateFilter: React.FC<DateFilterProps> = ({
  groupTitle,
  initialStartDate,
  initialEndDate,
  onSetStartDate,
  onSetEndDate,
  onSubmit,
  fromDateLabel = undefined,
  toDateLabel = undefined,
}) => {
  const { t } = useTranslation('filter');
  const isMobile = useIsMobile();

  const epoch = new Date('1970-01-01');
  const now = new Date();

  const [startDate, setStartDate] = useState<Date>(
    initialStartDate ?? addDays(new Date(), -7)
  );
  const [endDate, setEndDate] = useState<Date>(initialEndDate ?? new Date());
  const datePickerFormat = useLocaleDatePickerFormat();

  const handleDateChange = (date: Date | [Date, Date] | null, dateType: 'to' | 'from') => {
    if (date && !isArray(date)) {
      switch (dateType) {
        case 'from':
          setStartDate(date);
          onSetStartDate && onSetStartDate(date);
          break;
        case 'to':
          setEndDate(date);
          onSetEndDate && onSetEndDate(date);
          break;
      }
    }
  };

  const handleSubmit = () => {
    onSetStartDate && onSetStartDate(startDate);
    onSetEndDate && onSetEndDate(endDate);
    onSubmit?.();
  };

  return (
    <Grid gridGap={2}>
      {groupTitle && groupTitle !== '' && (
        <p>
          <strong>{groupTitle}</strong>
        </p>
      )}
      <FormProvider mode='onChange'>
        <Form id='report-dates-form' onValidSubmit={handleSubmit}>
          <Flex gridGap={6}>
            <FormControl isRequired={isMobile}>
              <FormLabel id='date_from_label'>
                {fromDateLabel ?? t('date_range_from_title', 'From')}:
              </FormLabel>
              <DatePicker
                aria-labelledby='date_from_label'
                dateFormat={datePickerFormat.dateFormat}
                placeholderText={datePickerFormat.placeHolderText}
                minDate={epoch}
                maxDate={endDate ?? now}
                yearDropDownFromTo={[
                  epoch.getFullYear(),
                  endDate?.getFullYear() ?? now.getFullYear(),
                ]}
                onChange={(date: unknown) => handleDateChange(date as Date, 'from')}
                selected={startDate}
                popperModifiers={{
                  preventOverflow: {
                    enabled: true,
                  },
                }}
                left={0}
                w={{ base: '100%', lg: '180px' }}
              />
            </FormControl>
            <FormControl>
              <FormLabel id='date_to_label'>
                {toDateLabel ?? t('date_range_to_title', 'To')}:{' '}
              </FormLabel>
              <DatePicker
                aria-labelledby='date_to_label'
                dateFormat={datePickerFormat.dateFormat}
                placeholderText={datePickerFormat.placeHolderText}
                minDate={startDate ?? epoch}
                maxDate={now}
                yearDropDownFromTo={[
                  startDate?.getFullYear() ?? epoch.getFullYear(),
                  now.getFullYear(),
                ]}
                onChange={(date: unknown) => handleDateChange(date as Date, 'to')}
                selected={endDate}
                popperModifiers={{
                  preventOverflow: {
                    enabled: true,
                  },
                }}
                left={0}
                w={{ base: '100%', lg: '180px' }}
              />
            </FormControl>
          </Flex>
        </Form>
      </FormProvider>
    </Grid>
  );
};
